import type { BulletsStaticContent } from '@core-api/editor/types/response';
import type { Question } from '@mentimeter/legacy-series-types/question';
import type { Slide as VotingSlide } from '../../compatibility-overrides/voting-types';
import { getOrSetCache, type WithCache } from '../../utils/get-or-set-cache';
import { isContentType } from '../../utils/is-content-type';
import { MismatchedSlideTypePropertyError } from '../errors/mismatched-slide-type-property-error';
import type { CompatibilitySlide } from '../../compatibility-types';
import type { CommonQuestionProperty } from './get-common-question-property';

const staticContentSchemaType: BulletsStaticContent['type'] = 'list';

export const getQuestionBulletsProperty = (
  target: CompatibilitySlide | VotingSlide,
  prop: keyof Question,
) => {
  const staticContents = target.staticContent;

  if (
    isContentType<BulletsStaticContent>(staticContents, staticContentSchemaType)
  ) {
    switch (prop as keyof Omit<Question, CommonQuestionProperty>) {
      case 'question':
        return staticContents.heading;

      case 'choices':
        const choices = staticContents.list.items.map<
          Question['choices'][number]
        >(({ id, content }, index) => ({
          id: id as unknown as number,
          label: content,
          correct_answer: false,
          position: index + 1,
        }));
        const cacheKey = JSON.stringify(choices);
        return getOrSetCache(
          target as WithCache<CompatibilitySlide>,
          'choices',
          cacheKey,
          choices,
        );

      case 'numbered_list':
        return staticContents.listStyling.listStyle === 'numbered';

      case 'steps':
        return staticContents.list.items.length;

      case 'show_all_bullets':
        return staticContents.listStyling.listDisplayMode === 'simultaneous';

      default:
        return undefined;
    }
  }
  throw new MismatchedSlideTypePropertyError(
    staticContentSchemaType,
    target.staticContent.type,
  );
};
